import React, { useState } from 'react';
import { useParams } from 'react-router';

// Types
import { FormTypeType, QuestionTypeType } from 'Shared/types/ReviewFormTemplate.type';

// Utils
import { isEmpty } from 'Wpds/components/rich-text/utils/EditorState.utils';

// Hooks
import { useReview } from 'Shared/utils/hooks/use-review';

// Helpers
import * as ReviewFormTemplatesHelpers from 'Shared/model_helpers/ReviewFormTemplates.helper';
import * as ReviewsHelpers from 'Shared/model_helpers/Reviews.helper';

// Components
import { WpText } from 'Wpds/components/text';
import { WpIcon } from 'Wpds/components/icon';
import FormQuestion from '../../ThreeSixtyReviewPartials/TakeQuestion/FormQuestion';
import IndividualResposnesAlert from '../_partials/IndividualResposnesAlert';
import WpAlert from 'Wpds/components/alert/WpAlert';
import { WpButton } from 'Wpds/components/button';
import WpInlineError from 'Wpds/components/inline-error/WpInlineError';

// Styles
import './QuestionnaireStep.scss';

type Props = {
  review;
  peerReviewForm;
  answers;
  formData;
  handleSetAnswers: Function;
  moveToNextStep: Function;
  isPreview?: boolean;
  isSelfPeerReview?: boolean;
};

const QuestionnaireStep = ({
  review,
  peerReviewForm,
  answers,
  formData,
  handleSetAnswers,
  moveToNextStep,
  isPreview = false,
  isSelfPeerReview,
}: Props) => {
  const [showErrors, setShowErrors] = useState(false);
  const { formSlug } = useParams();
  const reviewSlug = ReviewsHelpers.getSlug(review);
  const { isPerformanceReview } = useReview({ slug: reviewSlug });

  const questions = ReviewFormTemplatesHelpers.getQuestions(peerReviewForm);
  const formType = ReviewFormTemplatesHelpers.getFormType(peerReviewForm);
  const receiverSlug = ReviewsHelpers.getReceiverSlug(review);

  const humanReadableType = ((): string => {
    switch (formType) {
      case FormTypeType.manager_peer_review:
        return 'manager ';
      case FormTypeType.peer_review:
        return 'peer ';
      case FormTypeType.self_peer_review:
        return 'self ';
      default:
        return '';
    }
  })();
  const previewMessage = `Previewing ${humanReadableType}questions`;
  const showAlert = humanReadableType === 'manager ' && !isPreview;

  const questionErrorValidations = (() => {
    return questions.map(question => {
      const questionType = ReviewFormTemplatesHelpers.getQuestionType(question);
      const questionKey = ReviewFormTemplatesHelpers.getQuestionKey(question);
      const optional = ReviewFormTemplatesHelpers.getQuestionOptional(question);

      if (optional) return false;

      switch (questionType) {
        case QuestionTypeType.TITLE_DESCRIPTION:
          return false;
        case QuestionTypeType.TEXTAREA:
          return isEmpty(answers[questionKey]?.text);
        case QuestionTypeType.GIF: {
          const hasGif = Boolean(answers[questionKey]?.gif_url);
          return !hasGif;
        }
        case QuestionTypeType.ADJECTIVES_FOR_PROPERTY:
          return answers[questionKey]?.adjectives.length === 0 || isEmpty(answers[questionKey]?.explanation);
        case QuestionTypeType.RATING: {
          const rating = answers[questionKey]?.rating;
          const showExplanation = ReviewFormTemplatesHelpers.getQuestionShowExplanation(question) !== false;
          return (isEmpty(answers[questionKey]?.explanation) && showExplanation) || !rating;
        }
        default:
          return;
      }
    });
  })();

  const hasFormErrors = questionErrorValidations.toJS().filter(v => v === true).length > 0;

  const handleContinueClick = () => {
    if (!isPreview && hasFormErrors) {
      setShowErrors(true);
    } else moveToNextStep();
  };

  if (!peerReviewForm) return null;

  const baseClassName = 'QuestionnaireStep';
  return (
    <div className={baseClassName}>
      <div className={`${baseClassName}__title-wrapper`}>
        <h5>{isPerformanceReview ? 'Write review' : 'Provide Feedback'}</h5>
        {isPreview && (
          <div className={`${baseClassName}__title-wrapper__preview-msg`}>
            <span className={`${baseClassName}__title-wrapper__preview-msg__icon`}>
              <WpIcon name="eye" />
            </span>
            <WpText variant="bodySm" noMargin color="gray-600">
              {previewMessage}
            </WpText>
          </div>
        )}
      </div>
      {showAlert && (
        <div className={`${baseClassName}__content__alert`}>
          <WpAlert type="warning" icon={<WpIcon name="eye"></WpIcon>}>
            Direct reports will be able to see their manager’s reviews once shared.
          </WpAlert>
        </div>
      )}
      <div className={`${baseClassName}__questions`}>
        {!isPreview && <IndividualResposnesAlert review={review} isSelfPeerReview={isSelfPeerReview} />}
        {questions.map((question, index) => {
          const quetionkey = ReviewFormTemplatesHelpers.getQuestionKey(question);
          return (
            <div className={`${baseClassName}__questions__question`} key={`${reviewSlug}-${formSlug}-${quetionkey}`}>
              {showErrors && questionErrorValidations.toJS()[index] && (
                <div className={`${baseClassName}__error`}>
                  <span className={`${baseClassName}__error__icon`}>
                    <WpIcon name="exclamation-circle" />
                  </span>
                  <WpInlineError error="Incomplete question" />
                </div>
              )}
              <FormQuestion
                receiverSlug={receiverSlug}
                question={question}
                isOpen
                answers={answers}
                formData={formData}
                handleSetAnswers={handleSetAnswers}
                isPreview={isPreview}
              />
            </div>
          );
        })}
      </div>
      {showErrors && hasFormErrors && (
        <div className={`${baseClassName}__incomplete-alert`}>
          <div className={`${baseClassName}__error`}>
            <span className={`${baseClassName}__error__icon`}>
              <WpIcon name="exclamation-circle" />
            </span>
            <WpInlineError error="You have incomplete questions. Please finish the questionnaire before continuing." />
          </div>
        </div>
      )}
      <div className={`${baseClassName}__footer`}>
        <WpButton onClick={handleContinueClick} size="lg">
          Continue
        </WpButton>
      </div>
    </div>
  );
};

export default QuestionnaireStep;
